
import {PublisherInterface} from "./publisher";
import logging from "../utils/log";

interface DeviceInfo
{
	mdi : MediaDeviceInfo;
	name : string;
}

export class WebRtcPublisher implements PublisherInterface
{
	streamInfo: {
		applicationName: string;
		streamName: string;
		sessionId: string;
	};
	private TAG = "[WebRtcPublisher]";

	private mediaStream: MediaStream;
	private ws : WebSocket;
	private pc : RTCPeerConnection;

	private cams : DeviceInfo[] = [];
	private mics : DeviceInfo[] = [];
	private wsConnection: WebSocket;
	private peerConnection : RTCPeerConnection;
	private video: HTMLVideoElement;

	private peerConnectionConfig = {'iceServers': []};
	private muted: boolean;
	private cam: number;
	private mic: number;

	constructor(private $root : JQuery)
	{
		logging.log(this.TAG, "constructed");
	}

	public init() : Promise<void>
	{
		logging.log(this.TAG, "init...");
		this.video = document.createElement("video");
		this.video.muted = true;
		this.video.autoplay = true;
		this.$root.find(".sxcccp-video").empty().append(this.video);
		logging.log(this.TAG, "init2...");
		return this.reloadDevices().then(() => {
			logging.log(this.TAG, "init done");
		});
	}

	private reloadDevices()
	{
		logging.log(this.TAG, "reloadDevices");

		return navigator.mediaDevices.enumerateDevices().then((devices : MediaDeviceInfo[]) =>
		{
			logging.log(this.TAG, "reloadDevices got data", devices);

			const cams = [];
			const mics = [];

			for (let device of devices)
			{
				switch (device.kind) {
					case "audioinput": {
						mics.push({
							mdi: device,
							name: device.label || ("Mic " + (this.mics.length + 1))
						});
						break;
					}

					case "videoinput": {
						cams.push({
							mdi: device,
							name: device.label || ("Cam " + (this.cams.length + 1))
						})
					}
				}
			}

			this.cams = cams;
			this.mics = mics;
		});
	}

	public getCamList(): string[]
	{
		return this.cams.map(x => x.name);
	}

	public getMicList(): string[]
	{
		return this.mics.map(x => x.name);
	}

	public mute(muted: boolean): void
	{
		this.muted = muted;

		if (this.mediaStream) {
			for (let track of this.mediaStream.getAudioTracks()) {
				track.enabled = !this.muted;
			}
		}
	}

	public takeSnapshot(quality: number): string
	{
		if (!this.video) return null;

		const canvas = document.createElement('canvas');
		canvas.width  = this.video.videoWidth;
		canvas.height = this.video.videoHeight;
		const ctx = canvas.getContext('2d');
		ctx.drawImage(this.video, 0, 0);

		let data = canvas.toDataURL('image/jpeg').split(",", 2);
		return data[1];
	}

	public showPreview(show: boolean): void
	{
		$(this.video)[show ? "show" : "hide"]();
	}

	public publish(streamParams, camID = undefined, micID = undefined): Promise<any>
	{
		let promiseState = null;

		logging.log(this.TAG, "publish...");

		return new Promise((resolve, reject) =>
		{
			this.initStream(camID, micID).then(() =>
			{
				logging.log(this.TAG, "publish", "stream initialized");

				this.streamInfo = {
					applicationName : streamParams.applicationName,
					streamName : streamParams.streamName,
					sessionId : "[empty]"
				};

				this.wsConnection = new WebSocket(streamParams.socketURL);
				this.wsConnection.binaryType = 'arraybuffer';

				this.wsConnection.onopen = () =>
				{
					logging.log(this.TAG, "publish", "wsConnection is opened");

					this.peerConnection = new RTCPeerConnection(this.peerConnectionConfig);

					this.peerConnection.onicecandidate = (e) => {
						logging.log(this.TAG, "publish", 'gotIceCandidate: ', e.candidate);
					};

					if ("addTrack" in this.peerConnection)
					{
						for(let track of this.mediaStream.getTracks()) {
							this.peerConnection["addTrack"](track, this.mediaStream);
						}
					}
					else
					{
						// @ts-ignore
						this.peerConnection.addStream(this.mediaStream);
					}

					this.peerConnection.createOffer().then((desc) =>
					{
						logging.log(this.TAG, "publish", 'gotDescription: ', desc);

						return this.peerConnection.setLocalDescription(desc).then(() =>
						{
							this.wsConnection.send(JSON.stringify({
								direction: "publish",
								command: "sendOffer",
								streamInfo: this.streamInfo,
								sdp: desc,
								userData: {
									// TODO: User data
								}
							}));
						}).catch((e) => {
							logging.error(
								this.TAG,
								"publish",
								"Failed to set descriptor",
								e);

							promiseState = "reject";
							reject("Peer connection failed");
						});
					});
				};

				this.wsConnection.onmessage = (evt) =>
				{
					logging.log(this.TAG, "publish", "wsConnection.onmessage: ", evt.data);

					const msgJSON = JSON.parse(evt.data);
					const msgStatus = Number(msgJSON['status']);
					const msgCommand = msgJSON['command'];

					if (msgStatus != 200)
					{
						promiseState = "reject";
						reject("Invalid message from webrtc: " + evt.data);
						return;
					}

					const sdpData = msgJSON['sdp'];

					if (sdpData !== undefined)
					{
						logging.log(this.TAG, 'sdp: ', msgJSON['sdp']);

						const data = new RTCSessionDescription(msgJSON["sdp"]);

						this.peerConnection.setRemoteDescription(data).then(() => {
							//peerConnection.createAnswer(gotDescription, errorHandler4);
						}).catch((x) => {
							promiseState = "rejected";
							reject("Failed to set remote description:" + JSON.stringify(x));
							logging.log("fail", x)
						});
					}

					const iceCandidates = msgJSON['iceCandidates'];
					if (iceCandidates !== undefined)
					{
						for(let index in iceCandidates)
						{
							logging.log('iceCandidates: '+iceCandidates[index]);
							this.peerConnection.addIceCandidate(new RTCIceCandidate(iceCandidates[index]));
						}
					}

					promiseState = "resolved";
					resolve();
				};

				this.wsConnection.onclose = () => {
					logging.log(this.TAG, "publish", "wsConnection closed");
					if (!promiseState) {
						reject("ws connection closed");
					}
				};

				this.wsConnection.onerror = (e) =>
				{
					logging.error(this.TAG, "publish", "wsConnection.error", e);

					if (!promiseState) {
						reject("ws connection failed");
					}
				}
			}
			).catch((e) =>
			{
				logging.error(this.TAG, "publish", "Failed to initialize stream", e)
			});
		});
	}

	private initStream(camID, micID) : Promise<any>
	{
		logging.log(this.TAG, "initStream");
		let videoSource = this.cams[camID]
			? this.cams[camID].mdi.deviceId
			: this.cams.length ? this.cams[0].mdi.deviceId : undefined;

		let audioSource = this.mics[micID]
			? this.mics[micID].mdi.deviceId
			: this.mics.length ? this.mics[0].mdi.deviceId : undefined;

		const constrains : MediaStreamConstraints = {
			video: {
				width: {
					min: 320,
					ideal: 384,
					max: 640,
				},
				height: {
					min: 180,
					ideal: 216,
					max: 360,
				},
				frameRate: {
					ideal: 15,
					max: 30
				},
				// facingMode: "user",
				aspectRatio: 16/9,
				deviceId: videoSource ? {exact: videoSource} : undefined
			},
			audio: {
				deviceId: audioSource ? {exact: audioSource} : undefined
			},
		};

		return navigator.mediaDevices.getUserMedia(constrains)
			.then((stream : MediaStream) => {
				logging.log(this.TAG, "initStream", "ready", stream);
				this.mediaStream = stream;
				this.video.src = window.URL.createObjectURL(stream);
				this.video.play();
				this.mute(this.muted);

			}).catch((e) => {
				logging.error(this.TAG, "initStream", "failed", e);
				return Promise.reject(e);
			})
	}

	askForCam() : Promise<any>
	{
		logging.log(this.TAG, "askForCam...");
		return this.initStream(null, null).then((x) => {
			logging.log(this.TAG, "askForCam resolved", x);
		});
	}

	public unpublish(): Promise<void>
	{
		if (this.wsConnection) {
			this.wsConnection.close();
			this.wsConnection = null;
		}

		if (this.video) {
			this.video.pause();
			this.video.src = "";
			this.video = null;
		}

		if (this.peerConnection)
		{
			// for (let stream of this.peerConnection.getLocalStreams())
			// {
			// 	if ("removeTrack" in this.peerConnection) {
			// 		for (let track of stream.getTracks()) {
			// 			this.peerConnection["removeTrack"](track);
			// 		}
			// 	}
			// 	if ("removeStream" in this.peerConnection) {
			// 		this.peerConnection.removeStream(stream);
			// 	}
			// }

			if (this.peerConnection.signalingState !== "closed") this.peerConnection.close();
			this.peerConnection = null;
		}

		if (this.mediaStream)
		{
			for (let track of this.mediaStream.getTracks()) {
				track.stop();
			}
			this.mediaStream = null;
		}

		return Promise.resolve();
	}

	public destroy() : Promise<void>
	{
		return this.unpublish().then(() => {
			this.$root.find(".sxcccp-video").empty();
		});
	}
}
